import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { ArrowLeft, Plus, Trash2, ArrowUpRight, Lock } from "lucide-react";
import { MdTrendingUp, MdAccountBalance, MdCallReceived, MdCallMade, MdLink } from "react-icons/md";
import { RiStockLine } from "react-icons/ri";
import ExcelUpload from "./ExcelUpload";           // ← NEW IMPORT

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const ARCHIVED_STATUSES = ["Settled", "Rolled Over", "Cancelled", "Extended"];

const CURRENCY_PAIRS = [
  "USD/INR", "EUR/INR", "GBP/INR", "JPY/INR", "AUD/INR", "CAD/INR", "CHF/INR", "NZD/INR",
  "EUR/USD", "GBP/USD", "USD/JPY", "USD/CHF", "AUD/USD", "USD/CAD", "NZD/USD", "EUR/GBP",
  "EUR/JPY", "GBP/JPY", "EUR/CHF", "GBP/CHF",
];

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "NZD", "SGD", "AED", "SAR"];

const BANKS = [
  "HDFC Bank", "ICICI Bank", "SBI", "Axis Bank", "Kotak Mahindra",
  "Yes Bank", "Citibank", "Standard Chartered", "HSBC",
  "Deutsche Bank", "Barclays", "BNP Paribas", "Other",
];

const INSTRUMENTS = [
  "Forward", "Option (Call/Put)", "Par Forward",
  "Seagull", "Range Forward", "Participating Forward",
];

const STATUS_COLORS = {
  "Active":      "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  "Settled":     "bg-gray-500/20 text-gray-400 border-gray-500/30",
  "Rolled Over": "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
  "Extended":    "bg-amber-500/20 text-amber-400 border-amber-500/30",
  "Cancelled":   "bg-red-500/20 text-red-400 border-red-500/30",
};

// ── INSTRUMENT COLUMN CONFIG ──────────────────────────────────────────────────
const INSTRUMENT_COLUMNS = {
  "Forward": {
    headers: ["Contract Ref", "Trade Date", "Currency Pair", "Direction", "Notional", "Forward Rate", "Maturity", "Bank", "Settlement","Underlying", "Status", ""],
    getValues: (r) => [
      r.contract_reference,
      fromISO(r.trade_date),
      r.currency_pair,
      r.direction,
      r.notional_amount_num?.toLocaleString() || r.notional_amount || "—",
      r.rate_locked_num || "—",
      fromISO(r.maturity_date),
      r.counterparty_bank,
      r.settlement_type,
      r.underlying_ref ? `${r.underlying_type || ''} ${r.underlying_ref}`.trim() : '—',
      r.status || "Active",
    ],
  },
  "Option (Call/Put)": {
    headers: ["Contract Ref", "Trade Date", "Currency Pair", "Type", "Direction", "Notional", "Strike Price", "Premium", "Expiry", "Bank", "Settlement", "Underlying","Status", ""],
    getValues: (r) => [
      r.contract_reference,
      fromISO(r.trade_date),
      r.currency_pair,
      r.option_type || "—",
      r.direction,
      r.notional_amount_num?.toLocaleString() || r.notional_amount || "—",
      r.strike_price || "—",
      r.premium_paid_num?.toLocaleString() || "—",
      fromISO(r.expiry_date) || fromISO(r.maturity_date),
      r.counterparty_bank,
      r.settlement_type,
      r.underlying_ref ? `${r.underlying_type || ''} ${r.underlying_ref}`.trim() : '—',
      r.status || "Active",
    ],
  },
  "Par Forward": {
    headers: ["Contract Ref", "Trade Date", "Currency Pair", "Direction", "Notional", "Par Forward Rate", "Premium", "Maturity", "Bank", "Settlement","Underlying", "Status", ""],
    getValues: (r) => [
      r.contract_reference,
      fromISO(r.trade_date),
      r.currency_pair,
      r.direction,
      r.notional_amount_num?.toLocaleString() || r.notional_amount || "—",
      r.rate_locked_num || "—",
      r.premium_paid_num?.toLocaleString() || "—",
      fromISO(r.maturity_date),
      r.counterparty_bank,
      r.settlement_type,
      r.underlying_ref ? `${r.underlying_type || ''} ${r.underlying_ref}`.trim() : '—',
      r.status || "Active",
    ],
  },
  "Seagull": {
    headers: ["Contract Ref", "Trade Date", "Currency Pair", "Direction", "Notional", "Buy Strike", "Sell Call", "Sell Put", "Net Premium", "Expiry", "Bank", "Underlying","Status", ""],
    getValues: (r) => [
      r.contract_reference,
      fromISO(r.trade_date),
      r.currency_pair,
      r.direction,
      r.notional_amount_num?.toLocaleString() || r.notional_amount || "—",
      r.strike_price || "—",
      r.barrier_high || "—",
      r.barrier_low || "—",
      r.premium_paid_num?.toLocaleString() || "0",
      fromISO(r.expiry_date) || fromISO(r.maturity_date),
      r.counterparty_bank,
      r.underlying_ref ? `${r.underlying_type || ''} ${r.underlying_ref}`.trim() : '—',
      r.status || "Active",
    ],
  },
  "Range Forward": {
    headers: ["Contract Ref", "Trade Date", "Currency Pair", "Direction", "Notional", "Floor Rate", "Ceiling Rate", "Expiry", "Bank", "Settlement", "Underlying","Status", ""],
    getValues: (r) => [
      r.contract_reference,
      fromISO(r.trade_date),
      r.currency_pair,
      r.direction,
      r.notional_amount_num?.toLocaleString() || r.notional_amount || "—",
      r.barrier_low || "—",
      r.barrier_high || "—",
      fromISO(r.expiry_date) || fromISO(r.maturity_date),
      r.counterparty_bank,
      r.settlement_type,
      r.underlying_ref ? `${r.underlying_type || ''} ${r.underlying_ref}`.trim() : '—',
      r.status || "Active",
    ],
  },
  "Participating Forward": {
    headers: ["Contract Ref", "Trade Date", "Currency Pair", "Direction", "Notional", "Forward Rate", "Participation %", "Expiry", "Bank", "Settlement", "Underlying","Status", ""],
    getValues: (r) => [
      r.contract_reference,
      fromISO(r.trade_date),
      r.currency_pair,
      r.direction,
      r.notional_amount_num?.toLocaleString() || r.notional_amount || "—",
      r.rate_locked_num || "—",
      r.participation_rate ? `${r.participation_rate}%` : "—",
      fromISO(r.expiry_date) || fromISO(r.maturity_date),
      r.counterparty_bank,
      r.settlement_type,
      r.underlying_ref ? `${r.underlying_type || ''} ${r.underlying_ref}`.trim() : '—',
      r.status || "Active",
    ],
  },
};

// ── DATE HELPERS ──────────────────────────────────────────────────────────────
function formatDateInput(value) {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 8)}`;
}

function toISO(ddmmyyyy) {
  if (!ddmmyyyy || ddmmyyyy.length !== 10) return null;
  const [dd, mm, yyyy] = ddmmyyyy.split("-");
  return `${yyyy}-${mm}-${dd}`;
}

function fromISO(isoDate) {
  if (!isoDate) return "";
  const [yyyy, mm, dd] = isoDate.split("-");
  return `${dd}-${mm}-${yyyy}`;
}

// ── INSTRUMENT FIELDS ─────────────────────────────────────────────────────────
function InstrumentFields({ instrument, form, setForm }) {
  const numField = (field, label, placeholder) => (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <input type="number" step="0.0001" value={form[field] || ""}
        onChange={e => setForm({ ...form, [field]: e.target.value })}
        className="nebula-input text-sm" placeholder={placeholder} />
    </div>
  );

  const selectField = (field, label, options) => (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <select value={form[field] || ""} onChange={e => setForm({ ...form, [field]: e.target.value })} className="nebula-input text-sm">
        <option value="">Select...</option>
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
    </div>
  );

  const dateField = (field, label, required = false) => (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label} {required && <span className="text-red-400">*</span>}</label>
      <input value={form[field] || ""}
        onChange={e => setForm({ ...form, [field]: formatDateInput(e.target.value) })}
        className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
    </div>
  );

  if (instrument === "Forward") return (
    <>
      {selectField("direction", "Direction", ["Buy", "Sell"])}
      {numField("rate_locked_num", "Forward Rate", "e.g. 83.50")}
      {numField("premium_paid_num", "Premium Paid", "e.g. 25000")}
      {selectField("settlement_type", "Settlement Type", ["Gross Settlement", "Net Settlement"])}
    </>
  );

  if (instrument === "Option (Call/Put)") return (
    <>
      {selectField("option_type", "Option Type", ["Call", "Put"])}
      {selectField("direction", "Buy/Sell", ["Buy", "Sell"])}
      {numField("strike_price", "Strike Price", "e.g. 84.00")}
      {numField("premium_paid_num", "Premium Paid", "e.g. 50000")}
      {dateField("expiry_date", "Expiry Date (DD-MM-YYYY)", true)}
      {selectField("settlement_type", "Settlement Type", ["Gross Settlement", "Net Settlement"])}
    </>
  );

  if (instrument === "Par Forward") return (
    <>
      {selectField("direction", "Direction", ["Buy", "Sell"])}
      {numField("rate_locked_num", "Par Forward Rate", "e.g. 83.75")}
      {numField("premium_paid_num", "Premium", "e.g. 10000")}
      {selectField("settlement_type", "Settlement Type", ["Gross Settlement", "Net Settlement"])}
    </>
  );

  if (instrument === "Seagull") return (
    <>
      {selectField("direction", "Direction", ["Buy", "Sell"])}
      {numField("strike_price", "Buy Call Strike", "e.g. 85.00")}
      {numField("barrier_high", "Sell Call Strike (Upper Barrier)", "e.g. 87.00")}
      {numField("barrier_low", "Sell Put Strike (Lower Barrier)", "e.g. 81.00")}
      {numField("premium_paid_num", "Net Premium", "e.g. 0")}
      {dateField("expiry_date", "Expiry Date (DD-MM-YYYY)", true)}
      {selectField("settlement_type", "Settlement Type", ["Gross Settlement", "Net Settlement"])}
    </>
  );

  if (instrument === "Range Forward") return (
    <>
      {selectField("direction", "Direction", ["Buy", "Sell"])}
      {numField("barrier_low", "Floor Rate", "e.g. 82.00")}
      {numField("barrier_high", "Ceiling Rate", "e.g. 86.00")}
      {numField("premium_paid_num", "Premium", "e.g. 0")}
      {dateField("expiry_date", "Expiry Date (DD-MM-YYYY)", true)}
      {selectField("settlement_type", "Settlement Type", ["Gross Settlement", "Net Settlement"])}
    </>
  );

  if (instrument === "Participating Forward") return (
    <>
      {selectField("direction", "Direction", ["Buy", "Sell"])}
      {numField("rate_locked_num", "Forward Rate", "e.g. 83.50")}
      {numField("participation_rate", "Participation Rate %", "e.g. 50")}
      {numField("premium_paid_num", "Premium", "e.g. 0")}
      {dateField("expiry_date", "Expiry Date (DD-MM-YYYY)", true)}
      {selectField("settlement_type", "Settlement Type", ["Gross Settlement", "Net Settlement"])}
    </>
  );

  return null;
}

// ── ARCHIVE TOGGLE BUTTON ─────────────────────────────────────────────────────
function ArchiveToggle({ showArchived, onToggle, archivedCount }) {
  return (
    <button
      onClick={onToggle}
      className={`flex items-center gap-2 text-sm px-4 py-2 rounded-xl border transition-all ${
        showArchived
          ? "bg-amber-500/20 border-amber-500/40 text-amber-400"
          : "bg-[#1a1d26] border-[#2a2d3a] text-gray-400 hover:text-white"
      }`}
    >
      {showArchived ? "📂 Archived View" : "🗄 View Archive"}
      {!showArchived && archivedCount > 0 && (
        <span className="bg-amber-500/30 text-amber-400 text-xs px-1.5 py-0.5 rounded-full font-bold">
          {archivedCount}
        </span>
      )}
    </button>
  );
}

// ── MAIN ANALYTICS ────────────────────────────────────────────────────────────
export default function Analytics({ companyId, readOnly = false, initialBox = null, onClearInitialBox,  preFillRef = null }) {
  const [activeBox, setActiveBox] = useState(initialBox);

  const boxes = [
    { id: "hedge",       title: "Hedge Position",   icon: RiStockLine,      color: "from-indigo-500 to-purple-600", desc: "Outstanding forward contracts and hedges" },
    { id: "cash",        title: "Cash in the Bank", icon: MdAccountBalance, color: "from-emerald-500 to-teal-600",  desc: "Bank account balances across all accounts" },
    { id: "receivables", title: "Receivables",      icon: MdCallReceived,   color: "from-cyan-500 to-blue-600",     desc: "Money owed to your company" },
    { id: "payables",    title: "Payables",         icon: MdCallMade,       color: "from-amber-500 to-orange-600",  desc: "Money your company owes" },
  ];

  // ── Route to sub-pages ──────────────────────────────────────────────────────
 if (activeBox === "hedge") return (
  <HedgePosition
    onBack={() => { setActiveBox(null); if (onClearInitialBox) onClearInitialBox(); }}
    companyId={companyId}
    readOnly={readOnly}
    autoOpenForm={initialBox === 'hedge'}
    preFillRef={preFillRef}
  preFillType="LC"
  />
);
  if (activeBox === "cash")         return <CashInBank          onBack={() => setActiveBox(null)} companyId={companyId} readOnly={readOnly} />;
  if (activeBox === "receivables")  return <ReceivablesPayables type="receivables" onBack={() => setActiveBox(null)} companyId={companyId} readOnly={readOnly} />;
  if (activeBox === "payables")     return <ReceivablesPayables type="payables"    onBack={() => setActiveBox(null)} companyId={companyId} readOnly={readOnly} />;

  // ── NEW: Excel Upload page ──────────────────────────────────────────────────
  if (activeBox === "excel-upload") return <ExcelUpload onBack={() => setActiveBox(null)} companyId={companyId} />;

  return (
    <div className="max-w-screen-xl mx-auto px-6 py-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Details</h2>
        <p className="text-gray-400 text-sm">Click any box to view, add or manage your financial positions.</p>
      </div>

      {/* Data module cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {boxes.map(box => (
          <div key={box.id} onClick={() => setActiveBox(box.id)} className="nebula-card p-8 cursor-pointer group">
            <div className="flex items-center gap-5 mb-4">
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${box.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                <box.icon size={32} color="white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{box.title}</h3>
                <p className="text-gray-400 text-sm mt-1">{box.desc}</p>
              </div>
            </div>
            <div className="mt-4 text-indigo-400 text-sm font-medium group-hover:text-indigo-300 flex items-center gap-1">
              Open <ArrowUpRight className="w-4 h-4" />
            </div>
          </div>
        ))}
      </div>

      {/* ── Excel Upload card — ABOVE Connect Systems ── */}
      {!readOnly && (
        <div
          onClick={() => setActiveBox("excel-upload")}
          className="nebula-card p-8 mb-6 cursor-pointer group"
        >
          <div className="flex items-center gap-5 mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <span className="text-3xl">📊</span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Import via Excel</h3>
              <p className="text-gray-400 text-sm mt-1">
                Bulk upload Hedge Positions, Cash Balances, Receivables or Payables from a spreadsheet
              </p>
            </div>
          </div>

          {/* Module chips */}
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { label: "Hedge Positions", color: "#6366f1" },
              { label: "Cash in Bank",    color: "#10b981" },
              { label: "Receivables",     color: "#06b6d4" },
              { label: "Payables",        color: "#f59e0b" },
            ].map(m => (
              <span
                key={m.label}
                className="px-3 py-1 rounded-full text-xs font-semibold border"
                style={{ background: m.color + "20", color: m.color, borderColor: m.color + "40" }}
              >
                {m.label}
              </span>
            ))}
          </div>

          <div className="mt-2 text-teal-400 text-sm font-medium group-hover:text-teal-300 flex items-center gap-1">
            Open Import <ArrowUpRight className="w-4 h-4" />
          </div>
        </div>
      )}

      {/* Connect Systems */}
      <div className="nebula-card p-8">
        <div className="flex items-center gap-5 mb-6">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-pink-600 flex items-center justify-center">
            <MdLink size={32} color="white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Connect Banking & ERP Systems</h3>
            <p className="text-gray-400 text-sm mt-1">Link your bank accounts or financial software for real-time data</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { name: "HDFC Bank",  emoji: "🏦" }, { name: "ICICI Bank", emoji: "🏦" },
            { name: "SBI",        emoji: "🏦" }, { name: "Axis Bank",  emoji: "🏦" },
            { name: "Kotak Bank", emoji: "🏦" }, { name: "Yes Bank",   emoji: "🏦" },
            { name: "SAP",        emoji: "💻" }, { name: "Tally",      emoji: "💻" },
            { name: "Zoho Books", emoji: "💻" }, { name: "QuickBooks", emoji: "💻" },
            { name: "Oracle",     emoji: "💻" }, { name: "Custom API", emoji: "🔗" },
          ].map((s, i) => (
            <div key={i} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a] hover:border-indigo-500/50 transition-all cursor-pointer group">
              <span className="text-2xl">{s.emoji}</span>
              <span className="text-xs text-gray-400 text-center group-hover:text-white transition-colors">{s.name}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-600/20 border border-indigo-500/30 text-indigo-400">Connect</span>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <p className="text-amber-400 text-xs">🔒 All connections use secure OAuth 2.0 — your credentials are never stored on our servers.</p>
        </div>
      </div>
    </div>
  );
}

// ── HEDGE POSITION ────────────────────────────────────────────────────────────
function HedgePosition({ onBack, companyId, readOnly = false, autoOpenForm = false, preFillRef = null, preFillType = null }) {
  const [records,      setRecords]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
 const [showForm, setShowForm] = useState(autoOpenForm);
  const [showArchived, setShowArchived] = useState(false);
  const [settlementRecord, setSettlementRecord] = useState(null);
  const [settlementForm,   setSettlementForm]   = useState({
    status: "", settlement_date: "", settlement_rate: "", notes: ""
  });
  const [form, setForm] = useState({
    contract_reference: "", currency_pair: "", instrument_type: "",
    direction: "", notional_amount: "", notional_amount_num: "",
    rate_locked: "", rate_locked_num: "", premium_paid: "",
    premium_paid_num: "", counterparty_bank: "", maturity_date: "",
    strike_price: "", barrier_high: "", barrier_low: "",
    participation_rate: "", option_type: "", expiry_date: "",
    settlement_type: "", trade_date: "",
    underlying_type: preFillType || '',
underlying_ref:  preFillRef  || '',
  });

  useEffect(() => { loadRecords(); }, [companyId]);

  const loadRecords = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("hedge_positions")
        .select("*")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });
      if (error) console.error("Error loading hedge positions:", error);
      else setRecords(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const activeRecords   = records.filter(r => !ARCHIVED_STATUSES.includes(r.status || "Active"));
  const archivedRecords = records.filter(r =>  ARCHIVED_STATUSES.includes(r.status || "Active"));
  const displayRecords  = showArchived ? archivedRecords : activeRecords;

  const grouped = INSTRUMENTS.reduce((acc, inst) => {
    acc[inst] = displayRecords.filter(r => r.instrument_type === inst);
    return acc;
  }, {});

  const handleSave = async () => {
    if (!form.contract_reference || !form.currency_pair || !form.instrument_type) {
      alert("Contract reference, currency pair and instrument type are required.");
      return;
    }
    if (!form.trade_date || form.trade_date.length !== 10) {
      alert("Trade date is required. Please enter in DD-MM-YYYY format.");
      return;
    }
    if (!form.maturity_date || form.maturity_date.length !== 10) {
      alert("Maturity date is required. Please enter in DD-MM-YYYY format.");
      return;
    }

    
   // ── Validation against LC/BC ─────────────────────────
let underlyingRef  = form.underlying_ref  || null;
let underlyingType = form.underlying_type || null;

if (underlyingType === 'LC' && underlyingRef) {
  const { data: lcData } = await supabase
    .from('import_financing')
    .select('issue_date, currency')
    .eq('lc_ref', underlyingRef)
    .eq('company_id', companyId)
    .maybeSingle();

  if (lcData) {
    // Direction check
    if (form.direction && form.direction !== 'Buy') {
      alert(`⚠ Wrong direction for LC hedge.\n\n${underlyingRef} is an import payable — hedge must be a BUY (buying FCY to pay supplier).\n\nYou selected SELL. Please correct the direction.`);
      return;
    }
    // Currency pair check
    const expectedPair = `${lcData.currency}/INR`;
    if (form.currency_pair && form.currency_pair !== expectedPair) {
      alert(`⚠ Wrong currency pair for LC hedge.\n\n${underlyingRef} is a ${lcData.currency} LC — hedge should be ${expectedPair}.\n\nYou selected ${form.currency_pair}. Please correct the currency pair.`);
      return;
    }
    // Date check
    if (form.trade_date && toISO(form.trade_date) < lcData.issue_date) {
      const ok = window.confirm(`⚠ Hedge trade date (${form.trade_date}) is before LC issue date (${fromISO(lcData.issue_date)}).\n\nDoes this hedge pertain to ${underlyingRef}?\n\nOK = link to LC anyway   |   Cancel = save without LC link`);
      if (!ok) { underlyingRef = null; underlyingType = null; }
    }
  }
}

if (underlyingType === 'BC' && underlyingRef) {
  const { data: bcData } = await supabase
    .from('import_financing_bc')
    .select('drawdown_date, currency')
    .eq('bc_ref', underlyingRef)
    .eq('company_id', companyId)
    .maybeSingle();

  if (bcData) {
    // Direction check
    if (form.direction && form.direction !== 'Buy') {
      alert(`⚠ Wrong direction for BC hedge.\n\n${underlyingRef} is an import payable — hedge must be a BUY (buying FCY to repay lender).\n\nYou selected SELL. Please correct the direction.`);
      return;
    }
    // Currency pair check
    const expectedPair = `${bcData.currency}/INR`;
    if (form.currency_pair && form.currency_pair !== expectedPair) {
      alert(`⚠ Wrong currency pair for BC hedge.\n\n${underlyingRef} is a ${bcData.currency} BC — hedge should be ${expectedPair}.\n\nYou selected ${form.currency_pair}. Please correct the currency pair.`);
      return;
    }
    // Date check
    if (form.trade_date && toISO(form.trade_date) < bcData.drawdown_date) {
      const ok = window.confirm(`⚠ Hedge trade date (${form.trade_date}) is before BC drawdown date (${fromISO(bcData.drawdown_date)}).\n\nDoes this hedge pertain to ${underlyingRef}?\n\nOK = link to BC anyway   |   Cancel = save without BC link`);
      if (!ok) { underlyingRef = null; underlyingType = null; }
    }
  }
}

setSaving(true);
    try {
      const { error } = await supabase.from("hedge_positions").insert({
        company_id:          companyId,
        contract_reference:  form.contract_reference,
        currency_pair:       form.currency_pair,
        instrument_type:     form.instrument_type,
        direction:           form.direction,
        notional_amount:     form.notional_amount_num,
        notional_amount_num: form.notional_amount_num ? parseFloat(form.notional_amount_num) : null,
        rate_locked:         form.rate_locked_num,
        rate_locked_num:     form.rate_locked_num     ? parseFloat(form.rate_locked_num)     : null,
        premium_paid:        form.premium_paid_num,
        premium_paid_num:    form.premium_paid_num    ? parseFloat(form.premium_paid_num)    : null,
        counterparty_bank:   form.counterparty_bank,
        strike_price:        form.strike_price        ? parseFloat(form.strike_price)        : null,
        barrier_high:        form.barrier_high        ? parseFloat(form.barrier_high)        : null,
        barrier_low:         form.barrier_low         ? parseFloat(form.barrier_low)         : null,
        participation_rate:  form.participation_rate  ? parseFloat(form.participation_rate)  : null,
        option_type:         form.option_type,
        settlement_type:     form.settlement_type,
        maturity_date:       toISO(form.maturity_date),
        expiry_date:         form.expiry_date ? toISO(form.expiry_date) : null,
        trade_date:          toISO(form.trade_date),
        underlying_type: underlyingType,
underlying_ref:  underlyingRef,
        status:              "Active",
      });
      if (error) console.error("Insert error:", error);
      await loadRecords();
      setForm({
        contract_reference: "", currency_pair: "", instrument_type: "",
        direction: "", notional_amount: "", notional_amount_num: "",
        rate_locked: "", rate_locked_num: "", premium_paid: "",
        premium_paid_num: "", counterparty_bank: "", maturity_date: "",
        strike_price: "", barrier_high: "", barrier_low: "",
        participation_rate: "", option_type: "", expiry_date: "",
        settlement_type: "", trade_date: "",
        underlying_type: "", underlying_ref: "",
      });
      setShowForm(false);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this hedge position?")) return;
    const { error } = await supabase.from("hedge_positions").delete().eq("id", id);
    if (error) console.error("Delete error:", error);
    else await loadRecords();
  };

  const handleSettlementSave = async () => {
    if (!settlementForm.status) { alert("Please select a status."); return; }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("hedge_positions")
        .update({
          status:       settlementForm.status,
          settled_date: settlementForm.settlement_date ? toISO(settlementForm.settlement_date) : null,
          settled_rate: settlementForm.settlement_rate ? parseFloat(settlementForm.settlement_rate) : null,
          notes:        settlementForm.notes || null,
        })
        .eq("id", settlementRecord.id);
      if (error) console.error("Settlement error:", error);
      await loadRecords();
      setSettlementRecord(null);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const renderInstrumentTable = (inst, instRecords) => {
    const config = INSTRUMENT_COLUMNS[inst];
    if (!config) return null;

    return (
      <div key={inst} className="nebula-card overflow-hidden">
        <div className="px-6 py-3 bg-[#12141a] border-b border-[#2a2d3a] flex items-center justify-between">
          <h3 className="text-sm font-semibold text-indigo-400 uppercase tracking-wider">{inst}</h3>
          <span className="text-xs text-gray-500">{instRecords.length} position{instRecords.length > 1 ? "s" : ""}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#2a2d3a]">
                {config.headers.map((h, idx) => (
                  <th key={idx} className="text-left text-xs text-gray-500 uppercase py-3 px-4 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {instRecords.map((r, i) => {
                const values = config.getValues(r);
                return (
                  <tr key={r.id} className={`border-b border-[#2a2d3a]/40 ${i % 2 === 0 ? "" : "bg-[#12141a]/40"}`}>
                    {values.map((val, idx) => {
                      const header = config.headers[idx];
                      if (idx === 0) return <td key={idx} className="py-3 px-4 text-indigo-400 font-medium whitespace-nowrap">{val}</td>;
                      if (header === "Currency Pair") return <td key={idx} className="py-3 px-4 text-white font-semibold">{val}</td>;
                      if (header === "Direction") return (
                        <td key={idx} className="py-3 px-4">
                          {val && <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${val === "Buy" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>{val}</span>}
                        </td>
                      );
                      if (header === "Type") return (
                        <td key={idx} className="py-3 px-4">
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-400">{val}</span>
                        </td>
                      );
                      if (header === "Settlement") return (
                        <td key={idx} className="py-3 px-4">
                          {val && <span className="px-2 py-0.5 rounded-full text-xs bg-blue-500/20 text-blue-400">{val}</span>}
                        </td>
                      );
                      if (header === "Status") return (
                        <td key={idx} className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_COLORS[val] || STATUS_COLORS["Active"]}`}>{val}</span>
                        </td>
                      );
                      return <td key={idx} className="py-3 px-4 text-gray-300 whitespace-nowrap">{val || "—"}</td>;
                    })}
                    <td className="py-3 px-4">
                      {!readOnly && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSettlementRecord(r)}
                            className="px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/40 text-xs font-medium transition-all"
                          >
                            Settle
                          </button>
                          <button onClick={() => handleDelete(r.id)} className="text-red-400 hover:text-red-300 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-screen-xl mx-auto px-6 py-6">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="p-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white transition-all">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-white">Hedge Positions</h2>
          <p className="text-gray-400 text-sm">
            {showArchived
              ? `${archivedRecords.length} archived position${archivedRecords.length !== 1 ? "s" : ""}`
              : `${activeRecords.length} active position${activeRecords.length !== 1 ? "s" : ""}`}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <ArchiveToggle showArchived={showArchived} onToggle={() => setShowArchived(a => !a)} archivedCount={archivedRecords.length} />
          {!showArchived && !readOnly && (
            <button onClick={() => setShowForm(!showForm)} className="nebula-btn-primary flex items-center gap-2 text-sm px-4 py-2">
              <Plus className="w-4 h-4" /> Add Hedge
            </button>
          )}
        </div>
      </div>

      {showArchived && (
        <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <p className="text-amber-400 text-sm font-medium">
            📂 Showing {archivedRecords.length} archived position{archivedRecords.length !== 1 ? "s" : ""} — Settled, Rolled Over, Extended or Cancelled
          </p>
        </div>
      )}

      {showForm && !showArchived && !readOnly && (
        <div className="nebula-card p-6 mb-6">
          <h3 className="text-lg font-bold text-white mb-4">New Hedge Position</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Contract Reference <span className="text-red-400">*</span></label>
              <input value={form.contract_reference} onChange={e => setForm({ ...form, contract_reference: e.target.value })} className="nebula-input text-sm" placeholder="e.g. FWD-2024-001" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Instrument Type <span className="text-red-400">*</span></label>
              <select value={form.instrument_type} onChange={e => setForm({ ...form, instrument_type: e.target.value })} className="nebula-input text-sm">
                <option value="">Select instrument...</option>
                {INSTRUMENTS.map(i => <option key={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Currency Pair <span className="text-red-400">*</span></label>
              <select value={form.currency_pair} onChange={e => setForm({ ...form, currency_pair: e.target.value })} className="nebula-input text-sm">
                <option value="">Select pair...</option>
                <optgroup label="INR Pairs">
                  {CURRENCY_PAIRS.filter(p => p.includes("INR")).map(p => <option key={p}>{p}</option>)}
                </optgroup>
                <optgroup label="G7 Pairs">
                  {CURRENCY_PAIRS.filter(p => !p.includes("INR")).map(p => <option key={p}>{p}</option>)}
                </optgroup>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Notional Amount <span className="text-red-400">*</span></label>
              <input type="number" value={form.notional_amount_num || ""} onChange={e => setForm({ ...form, notional_amount_num: e.target.value })} className="nebula-input text-sm" placeholder="e.g. 500000" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Trade Date <span className="text-red-400">*</span></label>
              <input value={form.trade_date || ""} onChange={e => setForm({ ...form, trade_date: formatDateInput(e.target.value) })} className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Maturity Date <span className="text-red-400">*</span></label>
              <input value={form.maturity_date} onChange={e => setForm({ ...form, maturity_date: formatDateInput(e.target.value) })} className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Counterparty Bank</label>
              <select value={form.counterparty_bank} onChange={e => setForm({ ...form, counterparty_bank: e.target.value })} className="nebula-input text-sm">
                <option value="">Select bank...</option>
                {BANKS.map(b => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div>
  <label className="block text-xs text-gray-400 mb-1">Underlying Type</label>
  <select value={form.underlying_type || ''} onChange={e => setForm({ ...form, underlying_type: e.target.value })} className="nebula-input text-sm">
    <option value="">Select...</option>
    <option>LC</option>
    <option>BC</option>
    <option>Receivable</option>
    <option>Payable</option>
    <option>Other</option>
  </select>
</div>
<div>
  <label className="block text-xs text-gray-400 mb-1">Underlying Reference</label>
  <input value={form.underlying_ref || ''} onChange={e => setForm({ ...form, underlying_ref: e.target.value })}
    className="nebula-input text-sm" placeholder="e.g. LC-2024-001" />
</div>
          </div>
          {form.instrument_type && (
            <div className="border-t border-[#2a2d3a] pt-4 mb-4">
              <p className="text-xs text-indigo-400 font-semibold uppercase tracking-wider mb-3">{form.instrument_type} Details</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InstrumentFields instrument={form.instrument_type} form={form} setForm={setForm} />
              </div>
            </div>
          )}
          <div className="flex gap-3 mt-2">
            <button onClick={handleSave} disabled={saving} className="nebula-btn-primary text-sm px-6 py-2 flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Save Position"}
            </button>
            <button onClick={() => setShowForm(false)} className="px-6 py-2 rounded-xl border border-[#2a2d3a] text-gray-400 hover:text-white text-sm transition-all">Cancel</button>
          </div>
        </div>
      )}

      {settlementRecord && !readOnly && (
        <div className="nebula-card p-6 mb-6 border border-emerald-500/30">
          <h3 className="text-lg font-bold text-white mb-1">Settle Hedge Position</h3>
          <p className="text-gray-400 text-sm mb-4">
            {settlementRecord.contract_reference} · {settlementRecord.currency_pair} · {settlementRecord.instrument_type}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Status <span className="text-red-400">*</span></label>
              <select value={settlementForm.status} onChange={e => setSettlementForm({ ...settlementForm, status: e.target.value })} className="nebula-input text-sm">
                <option value="">Select...</option>
                <option>Settled</option><option>Rolled Over</option><option>Extended</option><option>Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Settlement Date (DD-MM-YYYY)</label>
              <input value={settlementForm.settlement_date} onChange={e => setSettlementForm({ ...settlementForm, settlement_date: formatDateInput(e.target.value) })} className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Settlement Rate</label>
              <input type="number" step="0.0001" value={settlementForm.settlement_rate} onChange={e => setSettlementForm({ ...settlementForm, settlement_rate: e.target.value })} className="nebula-input text-sm" placeholder="e.g. 83.50" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Notes</label>
              <input value={settlementForm.notes} onChange={e => setSettlementForm({ ...settlementForm, notes: e.target.value })} className="nebula-input text-sm" placeholder="Optional notes" />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSettlementSave} disabled={saving} className="nebula-btn-primary text-sm px-6 py-2 flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Update Status"}
            </button>
            <button onClick={() => setSettlementRecord(null)} className="px-6 py-2 rounded-xl border border-[#2a2d3a] text-gray-400 hover:text-white text-sm transition-all">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : displayRecords.length === 0 ? (
        <div className="nebula-card text-center py-16 text-gray-500">
          <RiStockLine size={40} className="mx-auto mb-3 opacity-30" />
          <p>{showArchived ? "No archived positions." : "No active hedge positions. Click Add Hedge to get started."}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {INSTRUMENTS.map(inst => {
            const instRecords = grouped[inst];
            if (!instRecords || instRecords.length === 0) return null;
            return renderInstrumentTable(inst, instRecords);
          })}
        </div>
      )}
    </div>
  );
}

// ── CASH IN THE BANK ──────────────────────────────────────────────────────────
function CashInBank({ onBack, companyId, readOnly = false }) {
  const [records,  setRecords]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form,     setForm]     = useState({ bank_name: "", account_number: "", currency: "", current_balance: "", as_of_date: "" });

  useEffect(() => { loadRecords(); }, [companyId]);

  const loadRecords = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.from("cash_balances").select("*").eq("company_id", companyId).order("created_at", { ascending: false });
      if (error) console.error("Error loading cash balances:", error);
      else setRecords(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.bank_name || !form.currency || !form.current_balance) { alert("Bank name, currency and balance are required."); return; }
    if (form.as_of_date && form.as_of_date.length !== 10) { alert("Please enter date in DD-MM-YYYY format."); return; }
    setSaving(true);
    try {
      const { error } = await supabase.from("cash_balances").insert({
        company_id:      companyId,
        bank_name:       form.bank_name,
        account_number:  form.account_number,
        currency:        form.currency,
        current_balance: form.current_balance,
        as_of_date:      form.as_of_date ? toISO(form.as_of_date) : null,
      });
      if (error) console.error("Insert error:", error);
      await loadRecords();
      setForm({ bank_name: "", account_number: "", currency: "", current_balance: "", as_of_date: "" });
      setShowForm(false);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    const { error } = await supabase.from("cash_balances").delete().eq("id", id);
    if (error) console.error("Delete error:", error);
    else await loadRecords();
  };

  return (
    <div className="max-w-screen-xl mx-auto px-6 py-6">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="p-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white transition-all"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h2 className="text-2xl font-bold text-white">Cash in the Bank</h2>
          <p className="text-gray-400 text-sm">All bank account balances</p>
        </div>
        {!readOnly && (
          <button onClick={() => setShowForm(!showForm)} className="ml-auto nebula-btn-primary flex items-center gap-2 text-sm px-4 py-2">
            <Plus className="w-4 h-4" /> Add Account
          </button>
        )}
      </div>

      {showForm && !readOnly && (
        <div className="nebula-card p-6 mb-6">
          <h3 className="text-lg font-bold text-white mb-4">Add Bank Account</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Bank Name <span className="text-red-400">*</span></label>
              <select value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} className="nebula-input text-sm">
                <option value="">Select...</option>
                {BANKS.map(b => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Account Number</label>
              <input value={form.account_number} onChange={e => setForm({ ...form, account_number: e.target.value })} className="nebula-input text-sm" placeholder="e.g. XXXX1234" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Currency <span className="text-red-400">*</span></label>
              <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="nebula-input text-sm">
                <option value="">Select...</option>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Current Balance <span className="text-red-400">*</span></label>
              <input value={form.current_balance} onChange={e => setForm({ ...form, current_balance: e.target.value })} className="nebula-input text-sm" placeholder="e.g. 5000000" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">As of Date (DD-MM-YYYY)</label>
              <input value={form.as_of_date} onChange={e => setForm({ ...form, as_of_date: formatDateInput(e.target.value) })} className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave} disabled={saving} className="nebula-btn-primary text-sm px-6 py-2 flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Save"}
            </button>
            <button onClick={() => setShowForm(false)} className="px-6 py-2 rounded-xl border border-[#2a2d3a] text-gray-400 hover:text-white text-sm transition-all">Cancel</button>
          </div>
        </div>
      )}

      <div className="nebula-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : records.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <MdAccountBalance size={40} className="mx-auto mb-3 opacity-30" />
            <p>No bank accounts yet. Click Add Account to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2a2d3a] bg-[#12141a]">
                  {["Bank", "Account No.", "Currency", "Balance", "As of Date", ""].map(h => (
                    <th key={h} className="text-left text-xs text-gray-500 uppercase py-3 px-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => (
                  <tr key={r.id} className={`border-b border-[#2a2d3a]/40 ${i % 2 === 0 ? "" : "bg-[#12141a]/40"}`}>
                    <td className="py-3 px-4 text-white font-medium">{r.bank_name}</td>
                    <td className="py-3 px-4 text-gray-400">{r.account_number}</td>
                    <td className="py-3 px-4"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-400">{r.currency}</span></td>
                    <td className="py-3 px-4 text-emerald-400 font-semibold">{r.current_balance}</td>
                    <td className="py-3 px-4 text-gray-400">{fromISO(r.as_of_date)}</td>
                    <td className="py-3 px-4">
                      {!readOnly && <button onClick={() => handleDelete(r.id)} className="text-red-400 hover:text-red-300 transition-colors"><Trash2 className="w-4 h-4" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── RECEIVABLES & PAYABLES ────────────────────────────────────────────────────
function ReceivablesPayables({ type, onBack, companyId, readOnly = false }) {
  const [records,          setRecords]          = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [saving,           setSaving]           = useState(false);
  const [showForm,         setShowForm]         = useState(false);
  const [showArchived,     setShowArchived]     = useState(false);
  const [editRecord,       setEditRecord]       = useState(null);
  const [settlementRecord, setSettlementRecord] = useState(null);
  const [form,             setForm]             = useState({ party_name: "", invoice_number: "", currency: "", amount: "", due_date: "", status: "Outstanding" });
  const [settlementForm,   setSettlementForm]   = useState({ settled_amount: "", settled_date: "", status: "" });

  const isReceivable = type === "receivables";

  useEffect(() => { loadRecords(); }, [type, companyId]);

  const loadRecords = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.from(type).select("*").eq("company_id", companyId).order("created_at", { ascending: false });
      if (error) console.error(`Error loading ${type}:`, error);
      else setRecords(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const activeRecords   = records.filter(r => r.status !== "Settled");
  const archivedRecords = records.filter(r => r.status === "Settled");
  const displayRecords  = showArchived ? archivedRecords : activeRecords;

  const handleSave = async () => {
    if (!form.party_name || !form.invoice_number || !form.amount) { alert("Party name, invoice number and amount are required."); return; }
    if (form.due_date && form.due_date.length !== 10) { alert("Please enter date in DD-MM-YYYY format."); return; }
    setSaving(true);
    try {
      if (editRecord) {
        const { error } = await supabase.from(type).update({
          party_name: form.party_name, invoice_number: form.invoice_number,
          currency: form.currency, amount: form.amount,
          due_date: form.due_date ? toISO(form.due_date) : null, status: form.status,
        }).eq("id", editRecord.id);
        if (error) console.error("Update error:", error);
        setEditRecord(null);
      } else {
        const { error } = await supabase.from(type).insert({
          company_id: companyId, party_name: form.party_name,
          invoice_number: form.invoice_number, currency: form.currency,
          amount: form.amount, due_date: form.due_date ? toISO(form.due_date) : null,
          status: form.status,
        });
        if (error) console.error("Insert error:", error);
      }
      await loadRecords();
      setForm({ party_name: "", invoice_number: "", currency: "", amount: "", due_date: "", status: "Outstanding" });
      setShowForm(false);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleEdit = (record) => {
    setEditRecord(record);
    setForm({ party_name: record.party_name || "", invoice_number: record.invoice_number || "", currency: record.currency || "", amount: record.amount || "", due_date: fromISO(record.due_date) || "", status: record.status || "Outstanding" });
    setShowForm(true);
    setSettlementRecord(null);
  };

  const handleSettlementOpen = (record) => {
    setSettlementRecord(record);
    setSettlementForm({ settled_amount: record.settled_amount || "", settled_date: fromISO(record.settled_date) || "", status: record.status || "Outstanding" });
    setShowForm(false);
    setEditRecord(null);
  };

  const handleSettlementSave = async () => {
    if (!settlementForm.status) { alert("Please select a status."); return; }
    if (settlementForm.settled_date && settlementForm.settled_date.length !== 10) { alert("Please enter settlement date in DD-MM-YYYY format."); return; }
    setSaving(true);
    try {
      const { error } = await supabase.from(type).update({
        status:         settlementForm.status,
        settled_amount: settlementForm.settled_amount ? parseFloat(settlementForm.settled_amount) : null,
        settled_date:   settlementForm.settled_date ? toISO(settlementForm.settled_date) : null,
      }).eq("id", settlementRecord.id);
      if (error) console.error("Update error:", error);
      await loadRecords();
      setSettlementRecord(null);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    const { error } = await supabase.from(type).delete().eq("id", id);
    if (error) console.error("Delete error:", error);
    else await loadRecords();
  };

  const totalAmount      = activeRecords.reduce((s, r) => s + (parseFloat(r.amount?.toString().replace(/[^0-9.]/g, "")) || 0), 0);
  const totalSettled     = activeRecords.reduce((s, r) => s + (parseFloat(r.settled_amount) || 0), 0);
  const totalOutstanding = totalAmount - totalSettled;
  const overdueCount     = activeRecords.filter(r => r.status === "Overdue").length;

  return (
    <div className="max-w-screen-xl mx-auto px-6 py-6">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="p-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white transition-all"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h2 className="text-2xl font-bold text-white">{isReceivable ? "Receivables" : "Payables"}</h2>
          <p className="text-gray-400 text-sm">
            {showArchived ? `${archivedRecords.length} settled / archived` : `${activeRecords.length} active · ${overdueCount} overdue`}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <ArchiveToggle showArchived={showArchived} onToggle={() => { setShowArchived(a => !a); setShowForm(false); setSettlementRecord(null); }} archivedCount={archivedRecords.length} />
          {!showArchived && !readOnly && (
            <button
              onClick={() => { setShowForm(!showForm); setEditRecord(null); setSettlementRecord(null); setForm({ party_name: "", invoice_number: "", currency: "", amount: "", due_date: "", status: "Outstanding" }); }}
              className="nebula-btn-primary flex items-center gap-2 text-sm px-4 py-2"
            >
              <Plus className="w-4 h-4" /> Add {isReceivable ? "Receivable" : "Payable"}
            </button>
          )}
        </div>
      </div>

      {showArchived && (
        <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <p className="text-amber-400 text-sm font-medium">
            📂 Showing {archivedRecords.length} settled {isReceivable ? "receivable" : "payable"}{archivedRecords.length !== 1 ? "s" : ""}
          </p>
        </div>
      )}

      {!showArchived && activeRecords.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="nebula-card p-4 text-center"><p className="text-xs text-gray-400 mb-1">Total Active</p><p className="text-xl font-bold text-white">{totalAmount.toLocaleString()}</p></div>
          <div className="nebula-card p-4 text-center"><p className="text-xs text-gray-400 mb-1">Settled</p><p className="text-xl font-bold text-emerald-400">{totalSettled.toLocaleString()}</p></div>
          <div className="nebula-card p-4 text-center"><p className="text-xs text-gray-400 mb-1">Outstanding</p><p className="text-xl font-bold text-amber-400">{totalOutstanding.toLocaleString()}</p></div>
          <div className="nebula-card p-4 text-center"><p className="text-xs text-gray-400 mb-1">Overdue</p><p className="text-xl font-bold text-red-400">{overdueCount}</p></div>
        </div>
      )}

      {showForm && !showArchived && !readOnly && (
        <div className="nebula-card p-6 mb-6">
          <h3 className="text-lg font-bold text-white mb-4">{editRecord ? `Edit ${isReceivable ? "Receivable" : "Payable"}` : `Add ${isReceivable ? "Receivable" : "Payable"}`}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">{isReceivable ? "Customer" : "Supplier"} Name <span className="text-red-400">*</span></label>
              <input value={form.party_name} onChange={e => setForm({ ...form, party_name: e.target.value })} className="nebula-input text-sm" placeholder="Company name" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Invoice Number <span className="text-red-400">*</span></label>
              <input value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} className="nebula-input text-sm" placeholder="e.g. INV-2024-001" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Currency</label>
              <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className="nebula-input text-sm">
                <option value="">Select...</option>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Amount <span className="text-red-400">*</span></label>
              <input value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="nebula-input text-sm" placeholder="e.g. 50000" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Due Date (DD-MM-YYYY)</label>
              <input value={form.due_date} onChange={e => setForm({ ...form, due_date: formatDateInput(e.target.value) })} className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="nebula-input text-sm">
                <option>Outstanding</option><option>Overdue</option><option>Partially Settled</option><option>Settled</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave} disabled={saving} className="nebula-btn-primary text-sm px-6 py-2 flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : editRecord ? "Update" : "Save"}
            </button>
            <button onClick={() => { setShowForm(false); setEditRecord(null); }} className="px-6 py-2 rounded-xl border border-[#2a2d3a] text-gray-400 hover:text-white text-sm transition-all">Cancel</button>
          </div>
        </div>
      )}

      {settlementRecord && !showArchived && !readOnly && (
        <div className="nebula-card p-6 mb-6 border border-emerald-500/30">
          <h3 className="text-lg font-bold text-white mb-1">Update Settlement</h3>
          <p className="text-gray-400 text-sm mb-4">{settlementRecord.party_name} · {settlementRecord.invoice_number} · {settlementRecord.currency} {settlementRecord.amount}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Status <span className="text-red-400">*</span></label>
              <select value={settlementForm.status} onChange={e => setSettlementForm({ ...settlementForm, status: e.target.value })} className="nebula-input text-sm">
                <option value="">Select...</option><option>Outstanding</option><option>Overdue</option><option>Partially Settled</option><option>Settled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Amount Settled</label>
              <input type="number" value={settlementForm.settled_amount} onChange={e => setSettlementForm({ ...settlementForm, settled_amount: e.target.value })} className="nebula-input text-sm" placeholder="e.g. 25000" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Settlement Date (DD-MM-YYYY)</label>
              <input value={settlementForm.settled_date} onChange={e => setSettlementForm({ ...settlementForm, settled_date: formatDateInput(e.target.value) })} className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
            </div>
          </div>
          {settlementForm.settled_amount && settlementRecord.amount && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Settlement Progress</span>
                <span>{((parseFloat(settlementForm.settled_amount) / parseFloat(settlementRecord.amount.toString().replace(/[^0-9.]/g, ""))) * 100).toFixed(1)}%</span>
              </div>
              <div className="w-full bg-[#2a2d3a] rounded-full h-2">
                <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${Math.min(100, (parseFloat(settlementForm.settled_amount) / parseFloat(settlementRecord.amount.toString().replace(/[^0-9.]/g, ""))) * 100)}%` }} />
              </div>
            </div>
          )}
          <div className="flex gap-3 mt-4">
            <button onClick={handleSettlementSave} disabled={saving} className="nebula-btn-primary text-sm px-6 py-2 flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Update Settlement"}
            </button>
            <button onClick={() => setSettlementRecord(null)} className="px-6 py-2 rounded-xl border border-[#2a2d3a] text-gray-400 hover:text-white text-sm transition-all">Cancel</button>
          </div>
        </div>
      )}

      <div className="nebula-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : displayRecords.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <MdCallReceived size={40} className="mx-auto mb-3 opacity-30" />
            <p>{showArchived ? `No archived ${type} yet.` : `No active ${type} yet. Click Add to get started.`}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2a2d3a] bg-[#12141a]">
                  {[isReceivable ? "Customer" : "Supplier", "Invoice No.", "Currency", "Amount", "Settled", "Balance", "Due Date", "Status", ...(showArchived ? [] : ["Actions"])].map(h => (
                    <th key={h} className="text-left text-xs text-gray-500 uppercase py-3 px-4 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayRecords.map((r, i) => {
                  const amt     = parseFloat(r.amount?.toString().replace(/[^0-9.]/g, "")) || 0;
                  const settled = parseFloat(r.settled_amount) || 0;
                  const balance = amt - settled;
                  return (
                    <tr key={r.id} className={`border-b border-[#2a2d3a]/40 ${i % 2 === 0 ? "" : "bg-[#12141a]/40"}`}>
                      <td className="py-3 px-4 text-white font-medium">{r.party_name}</td>
                      <td className="py-3 px-4 text-indigo-400">{r.invoice_number}</td>
                      <td className="py-3 px-4"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-400">{r.currency}</span></td>
                      <td className="py-3 px-4 text-white font-semibold">{r.amount}</td>
                      <td className="py-3 px-4 text-emerald-400">{settled > 0 ? settled.toLocaleString() : "—"}</td>
                      <td className="py-3 px-4">
                        <span className={balance > 0 ? "text-amber-400 font-medium" : "text-emerald-400"}>{balance > 0 ? balance.toLocaleString() : "Cleared"}</span>
                      </td>
                      <td className="py-3 px-4 text-gray-400 whitespace-nowrap">{fromISO(r.due_date)}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                          r.status === "Overdue"           ? "bg-red-500/20 text-red-400 border-red-500/30" :
                          r.status === "Settled"           ? "bg-gray-500/20 text-gray-400 border-gray-500/30" :
                          r.status === "Partially Settled" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" :
                          "bg-blue-500/20 text-blue-400 border-blue-500/30"
                        }`}>{r.status}</span>
                      </td>
                      {!showArchived && !readOnly && (
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <button onClick={() => handleEdit(r)} className="px-2 py-1 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/40 text-xs font-medium transition-all">Edit</button>
                            <button onClick={() => handleSettlementOpen(r)} className="px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/40 text-xs font-medium transition-all">Settle</button>
                            <button onClick={() => handleDelete(r.id)} className="text-red-400 hover:text-red-300 transition-colors"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
